import { Grid, Header, Message } from "semantic-ui-react";
import { useEffect, useState } from "react";
import { useNavigate } from "../../shims/useNavigate";

import Button from "../UI/Button/Button";
import DetailsCard from "../../components/Property/DetailsCard";
import CustomImprovementsCard from "../../components/Property/CustomImprovementsCard";
import ImprovementsAvailableCard from "../../components/Property/ImprovementsAvailableCard";
import EnergyUsageCard from "../../components/Property/EnergyUsageCard";
import Co2EnergyOptimisationCard from "../../components/Property/Co2EnergyOptimisationCard";
import CompareUpdateModal from "../../components/Property/CompareUpdateModal";
import ConfirmationModal from "../../components/Modal/ConfirmationModal";
import CustomUpdateButton from "../../components/CustomImprovements/CustomUpdateButton";

import useUpdateTrigger from "../../hooks/useUpdateTrigger";
import Property, { PropertyResponse } from "../../rest/Property";
import { useRestClientContext } from "../../contexts/RestClientContext";
import Preloader from "../UI/Preloader/Preloader";
import DeletePropertyButton from "./DeletePropertyButton";
import PropertyImagesCard from "./PropertyImagesCard";
import UpdateSapVersionModal from "../Modal/UpdateSapVersionModal";
import { useUserContext } from "../../contexts/UserContext";
import LocationMap from "./LocationMap";
import AdminToolsCard from "./AdminToolsCard";

interface PropertyPanelProps {
	teamId: number;
	propertyId: number;
}

export default function PropertyPanel({ teamId, propertyId }: PropertyPanelProps) {
	const client = useRestClientContext();
	const navigate = useNavigate();
	const user = useUserContext();

	const [compareUpdateOpen, setCompareUpdateOpen] = useState(false);
	const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
	const [sapVersionModalOpen, setSapVersionModalOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const { trigger, triggerUpdate } = useUpdateTrigger();
	const [property, setProperty] = useState<Property>();
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		setLoading(true);
		client
			.fetchProperty(teamId, propertyId)
			.then((response: Response) => response.json())
			.then((json: PropertyResponse) => {
				const property = new Property(json);
				setProperty(property);
				setLoading(false);
			})
			.catch((error) => setError(error));
	}, [teamId, propertyId, client, trigger]);

	const onOpenCompareUpdate = () => {
		setCompareUpdateOpen(true);
	};

	const onDeleteProperty = () => {
		setConfirmDeleteModalOpen(true);
	};

	const onConfirmDeleteProperty = () => {
		setIsDeleting(true);

		client.deleteProperty(teamId, propertyId).then(() => {
			navigate("/");
		});
	};

	const openSapVersionChange = () => {
		setSapVersionModalOpen(true);
	};

	return loading || !property ? (
		<Preloader />
	) : error ? (
		<Message negative>{error.message}</Message>
	) : (
		<>
			<Header as="h1" className="address-header">
				{property.fullAddress}
			</Header>
			<LocationMap property={property} />
			{user.is_admin && <AdminToolsCard property={property} />}

			<DetailsCard property={property}>
				<div className={"button-row"}>
					{user.is_admin && (
						<Button variant="contained" onClick={openSapVersionChange}>
							Change SAP Version
						</Button>
					)}
					<Button variant="contained" onClick={onOpenCompareUpdate}>
						Compare &amp; Update
					</Button>
				</div>
			</DetailsCard>
			<CustomImprovementsCard property={property}>
				<CustomUpdateButton
					property={property}
					onUpdateProperty={() => {
						triggerUpdate();
						return null;
					}}
				/>
			</CustomImprovementsCard>
			<Grid className="charts">
				<Grid.Row>
					<Grid.Column>
						<EnergyUsageCard property={property} />
					</Grid.Column>
					<Grid.Column>
						<Co2EnergyOptimisationCard property={property} />
					</Grid.Column>
				</Grid.Row>
			</Grid>
			<ImprovementsAvailableCard property={property} />
			<PropertyImagesCard property={property} />
			<DeletePropertyButton onDelete={onDeleteProperty} />

			{compareUpdateOpen && (
				<CompareUpdateModal
					property={property}
					onClose={() => setCompareUpdateOpen(false)}
					onSuccess={() => {
						triggerUpdate();
						setCompareUpdateOpen(false);
					}}
				/>
			)}

			<UpdateSapVersionModal
				property={property}
				onClose={() => setSapVersionModalOpen(false)}
				isOpen={sapVersionModalOpen}
				onSuccess={() => {
					triggerUpdate();
				}}
			/>

			<ConfirmationModal
				open={confirmDeleteModalOpen}
				title="Confirm delete"
				confirmationText="Confirm"
				buttonsDisabled={isDeleting}
				onConfirm={onConfirmDeleteProperty}
				onCancel={() => setConfirmDeleteModalOpen(false)}
			>
				<p>Are you sure you want to delete this property?</p>
				<p>
					<strong>This action cannot be undone.</strong>
				</p>
			</ConfirmationModal>
		</>
	);
}
